// `import type`, so the bundler provably drops it. As a value import, Babel's elision is incidental
// rather than guaranteed — and this specifier is web's whole root reducer.
import type { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
// Aliased, not relative: `@AxiosWrapper` resolves to this same module on web but to the mobile
// axios instance on mobile. A relative path would drag web's axios — and through it web's store,
// router and MUI — into the React Native bundle. Every mobile-consumed action module does this.
import axiosWrapper from "@AxiosWrapper";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "../services/constants";
import { showToastNotification } from "../reducers/toastSlice";
import { setSpaceUserDetails } from "../reducers/projectUsers";
import { t, untranslated } from "@i18n";
import type {
  AddUserToSpaceRequest,
  AddUserToSpaceResponse,
  DeleteUserFromSpaceRequest,
  DeleteUserFromSpaceResponse,
} from "@ServerTypes/spaces/space.schema";
import type { AddUserToProjectRequest, AddUserToProjectResponse } from "@ServerTypes/projects/project.schema";
import type { GetAllUsersInSpaceResponse } from "@ServerTypes/accounts/accounts.schema";

// No call sites: every live "add user to space" path (SpaceUsersList, GroupsInSpace,
// UpdateDefaultAccessInSpace) posts to `/spaces/addUserToSpace` through `axiosWrapper` directly.
// The params are typed from the endpoint's own request schema rather than from usage.
// `metadata`/`refreshHandler`/`updatePermission` are optional because each is guarded before use.
export function addUserToSpace(
  data: AddUserToSpaceRequest,
  metadata?: { selectedAddUserName?: string; spaceName?: string },
  refreshHandler?: () => void,
  updatePermission?: boolean,
) {
  return (dispatch: AppDispatch) => {
    axiosWrapper.post<AddUserToSpaceResponse>(`${getBackendUrl()}/spaces/addUserToSpace`, data).then((response) => {
      metadata
        ? dispatch(
            showToastNotification({
              content: t("{{user}} added to Space {{space}} successfully!", {
                user: metadata.selectedAddUserName,
                space: metadata.spaceName,
              }),
              type: TOAST_TYPE_SUCCESS,
            }),
          )
        : null;
      if (updatePermission && response.data.status) {
        dispatch(
          showToastNotification({
            content: t("Permission changed successfully!"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else if (!response.data.status) {
        dispatch(
          showToastNotification({
            content: untranslated(response.data.errorMessage || SOMETHING_WENT_WRONG),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      if (refreshHandler) {
        refreshHandler();
      }
    });
  };
}
export const DeleteUserFromSpace = async (data: DeleteUserFromSpaceRequest) => {
  const response = await axiosWrapper.post<DeleteUserFromSpaceResponse>(
    `${getBackendUrl()}/spaces/DeleteUserFromSpace`,
    data,
  );
  return response;
};

// `source` is REQUIRED by the endpoint, but the sole caller threads it from an optional prop, so it
// is declared optional here rather than forced at the call site. Sending `undefined` is a live bug
// on that path (the server picks the permission to enforce from it); preserved verbatim.
export async function addUserToProject(
  data: Omit<AddUserToProjectRequest, "source"> & { source?: AddUserToProjectRequest["source"] },
) {
  const responseData = await axiosWrapper.post<AddUserToProjectResponse>(
    `${getBackendUrl()}/projects/addUserToProject`,
    data,
  );
  return responseData;
}

export function getAllUsersInSpace(data: { spaceId: string }) {
  // Typed by what it actually dispatches, not by web's AppDispatch: this thunk is also dispatched
  // from mobile (whose store has its own dispatch type), and both stores satisfy this signature.
  return async (dispatch: (action: ReturnType<typeof setSpaceUserDetails>) => unknown) => {
    await axiosWrapper
      .post<GetAllUsersInSpaceResponse>(`${getBackendUrl()}/accounts/getAllUsersInSpace`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(setSpaceUserDetails(response.data.result ?? []));
        }
      });
  };
}
